import React, { useState, useEffect } from 'react'
import {
  Box, Typography, Chip, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress
} from '@material-ui/core'
import axios from 'axios'

const factorColor = (f) => {
  if (f < 1.0) return '#4caf50'
  if (f <= 1.2) return '#ff9800'
  if (f <= 1.5) return '#e65100'
  return '#d32f2f'
}

const RateImpactDisplay = ({ serviceId, attributeValues, url }) => {
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)

  // Serialize attributeValues to avoid re-render loops from object reference changes
  const attrKey = JSON.stringify(attributeValues || {})

  useEffect(() => {
    if (!serviceId || !url) return
    if (!attributeValues || Object.keys(attributeValues).length === 0) {
      setResult(null)
      return
    }

    setLoading(true)
    axios.post(`${url}/api/bid-estimation/rate-rules/evaluate`, {
      serviceId,
      attributeValues
    })
      .then(res => setResult(res.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [serviceId, attrKey, url])

  if (loading) return <CircularProgress size={20} />

  if (!result || result.matchedRules.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
        No rate adjustments for current attributes.
      </Typography>
    )
  }

  return (
    <Box>
      {/* Factor summary chips */}
      <Box mb={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
        {result.laborFactor !== 1.0 && (
          <Chip
            size="small"
            label={`Labor: ${result.laborFactor.toFixed(2)}x`}
            style={{ backgroundColor: factorColor(result.laborFactor), color: '#fff', fontSize: 11 }}
          />
        )}
        {result.equipmentFactor !== 1.0 && (
          <Chip
            size="small"
            label={`Equipment: ${result.equipmentFactor.toFixed(2)}x`}
            style={{ backgroundColor: factorColor(result.equipmentFactor), color: '#fff', fontSize: 11 }}
          />
        )}
        {result.materialFactor !== 1.0 && (
          <Chip
            size="small"
            label={`Material: ${result.materialFactor.toFixed(2)}x`}
            style={{ backgroundColor: factorColor(result.materialFactor), color: '#fff', fontSize: 11 }}
          />
        )}
        {result.addHours > 0 && (
          <Chip size="small" label={`+${result.addHours} hrs`} style={{ fontSize: 11 }} />
        )}
        {result.addCost > 0 && (
          <Chip size="small" label={`+$${result.addCost}`} style={{ fontSize: 11 }} />
        )}
      </Box>

      {/* Matched rules table */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ fontSize: 11, padding: '4px 8px' }}>Rule</TableCell>
            <TableCell style={{ fontSize: 11, padding: '4px 8px' }}>Target</TableCell>
            <TableCell align="right" style={{ fontSize: 11, padding: '4px 8px' }}>Factor</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.matchedRules.map((rule, i) => (
            <TableRow key={i}>
              <TableCell style={{ fontSize: 12, padding: '4px 8px' }}>{rule.name}</TableCell>
              <TableCell style={{ fontSize: 12, padding: '4px 8px' }}>
                {(rule.effect?.target || '').replace('_', ' ')}
              </TableCell>
              <TableCell align="right" style={{ fontSize: 12, padding: '4px 8px' }}>
                {rule.effect?.factor !== 1.0 && `${rule.effect?.factor}x`}
                {rule.effect?.addHours > 0 && ` +${rule.effect.addHours}h`}
                {rule.effect?.addCost > 0 && ` +$${rule.effect.addCost}`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default RateImpactDisplay
